import React, { useEffect, useMemo, useState } from 'react';
import { fetchBcProductPick, fetchUnmappedIgnored } from '../services/api';
import { EditDialog, Field, ListCard, PageTitle, WriteOffNote, useRows, useSave, useWriteLive } from '../components/StaffList';

/**
 * Tools & Lists > Unmapped Report - Ignored Products: BigCommerce products left off the Unmapped
 * Products/Variants report email (Azure exit WS8). Reads sql01.BCUnmappedReportIgnoredProducts.
 * Saving is off until ws8_unmapped_ignore_live.
 */

const showDate = (ymd) => { if (!ymd) return ''; const [y, m, d] = ymd.split('-'); return `${m}/${d}/${y}`; };
const dash = (v) => v || '-';

export default function UnmappedIgnore() {
  const live = useWriteLive('ws8_unmapped_ignore');
  const [search, setSearch] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [products, setProducts] = useState([]);
  const [find, setFind] = useState('');
  const [edit, setEdit] = useState(null);
  const { rows, error, reload } = useRows(() => fetchUnmappedIgnored({ search, includeDeleted }), [search, includeDeleted]);
  const { save, saving, error: saveError, setError } = useSave(reload);

  useEffect(() => { fetchBcProductPick().then(setProducts).catch(() => {}); }, []);
  const matches = useMemo(() => {
    const q = find.trim().toLowerCase();
    return (q ? products.filter((p) => String(p.label).toLowerCase().includes(q) || String(p.value).includes(q)) : products).slice(0, 200);
  }, [find, products]);

  const open = (r) => {
    setError(null);
    setFind('');
    setEdit(r ? { id: r.id, bcProductId: r.bcProductId, bcProductName: r.bcProductName, note: r.note || '' } : { id: 0, bcProductId: '', bcProductName: '', note: '' });
  };
  const choose = (value) => {
    const p = products.find((x) => String(x.value) === value);
    setEdit({ ...edit, bcProductId: value, bcProductName: p ? p.label : edit.bcProductName });
  };
  const submit = async () => {
    if (!edit.bcProductId) return setError('Please select a BigCommerce product.');
    if (await save('unmapped_ignore_save', edit)) setEdit(null);
    return null;
  };
  const remove = (r) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(`Stop ignoring ${r.bcProductName || r.bcProductId}? It returns to the report on the next run.`)) save('unmapped_ignore_delete', { id: r.id });
  };

  return (
    <div>
      <PageTitle>Unmapped Report - Ignored Products</PageTitle>
      <div className="staff-hint">BigCommerce products listed here are left off the Unmapped Products/Variants report email. Use it for products that can never be mapped to a CPX catalog row, blog image hosts for example.</div>
      <WriteOffNote live={live} what="Adding, editing and removing" />
      <div className="port-filters">
        <input type="search" className="form-input" style={{ width: 'auto' }} placeholder="Product, id or reason" aria-label="Product, id or reason" value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="staff-check"><input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} /> Show removed</label>
        <button type="button" className="btn btn-primary" disabled={!live} onClick={() => open(null)}>Add Ignored Product</button>
      </div>
      <ListCard title="Ignored products" rows={rows} error={error} empty="No ignored products.">
        <table className="trans-table">
          <thead><tr><th>BC Product</th><th>BC Product Id</th><th>Reason</th><th>Added By</th><th>Added</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id} className={r.deletedAt ? 'staff-row-off' : ''}>
                <td>{dash(r.bcProductName)}</td>
                <td>{r.bcProductId}</td>
                <td>{dash(r.note)}</td>
                <td>{dash(r.createdBy)}</td>
                <td>{showDate(r.createdAt)}</td>
                <td className="staff-actions">
                  {r.deletedAt ? `removed ${showDate(r.deletedAt)}` : (
                    <>
                      <button type="button" className="btn btn-ghost" disabled={!live || saving} onClick={() => open(r)}>Edit</button>
                      <button type="button" className="btn btn-ghost" disabled={!live || saving} onClick={() => remove(r)}>Remove</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListCard>
      {!edit && saveError && <div className="staff-msg staff-msg-error">{saveError}</div>}
      {edit && (
        <EditDialog title={edit.id ? 'Edit Ignored Product' : 'Add Ignored Product'} live={live} saving={saving} error={saveError} onClose={() => setEdit(null)} onSave={submit}>
          <Field label="Find a BigCommerce product"><input className="form-input" value={find} onChange={(e) => setFind(e.target.value)} /></Field>
          <Field label="BigCommerce Product">
            <select className="form-select" value={edit.bcProductId} onChange={(e) => choose(e.target.value)}>
              <option value="">Select BC Product</option>
              {edit.bcProductId && !matches.some((p) => String(p.value) === String(edit.bcProductId)) && <option value={edit.bcProductId}>{edit.bcProductName || `BC Product ${edit.bcProductId}`}</option>}
              {matches.map((p) => <option key={`${p.value}-${p.label}`} value={p.value}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Reason (optional)"><textarea className="form-textarea" maxLength={500} value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} /></Field>
        </EditDialog>
      )}
    </div>
  );
}
